/* eslint-disable import/no-cycle */
/* eslint-disable no-param-reassign */
import { downLoad } from '$/utils/download';
import React, {
  forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState,
} from 'react';
import XGPlayer, { Events } from 'xgplayer';
import { mtstatTrack } from '$/utils/mtstat';
import 'xgplayer/dist/index.min.css'; // 默认样式
import { handleImageUrl, preloadImages } from '$/utils/image';
import type { PreviewVideoPlaybackHandoff } from '$/pages/RoboneoChatBoard/type';
import { WatermarkState } from '../DownloadPreferencePopup';

interface StatParams {
  [key: string]: any;
  task_id?: string;
  hasWatermark?: WatermarkState; // 0: 无水印，1: 有水印，2: 关闭下载
}
interface Controller extends ReturnType<typeof useCustomControls> {
  onDownload: (statParams: StatParams) => void;
}
/** `VideoCom` forwardRef 暴露给父组件的结构（读取底层播放器、容器等） */
export type VideoComHandle = {
  containerRef: HTMLDivElement | null;
  playerRef: XGPlayer | null;
  videoRef: HTMLDivElement | null;
};

interface VideComProps {
  url: string;
  watermarkUrl?: string;
  className?: string;
  /** 透传到底层 video 元素的 crossOrigin 属性 */
  crossOrigin?: '' | 'anonymous' | 'use-credentials';
  /** 兼容小写写法，内部会与 crossOrigin 合并使用 */
  crossorigin?: '' | 'anonymous' | 'use-credentials';
  poster?: string;
  watermarkPoster?: string;
  posterSize?: number;
  videoClassName?: string;
  autoplay?: boolean;
  loop?: boolean;
  children?: (controller: Controller) => React.ReactNode;
  volume?: number;
  videoInit?: boolean;
  containerStyle?: React.CSSProperties;
  closeVideoClick?: boolean;
  width?: number | string;
  height?: number | string;
  draggable?: boolean; // 视频拖拽属性
  fitVideoSize?: 'fixWidth';
  /** 视频在容器内的填充方式，默认 contain（易留黑边）；缩略块等场景可用 cover 铺满 */
  videoFillMode?: 'auto' | 'fillHeight' | 'fillWidth' | 'fill' | 'cover' | 'contain';
  /**
   * 播放器就绪且拿到有效 duration 后仅应用一次：跳转到 startSec，并按需自动播放；可含 muted 与页内一致
   */
  playbackHandoff?: PreviewVideoPlaybackHandoff | null;
  /** 与 usePreviewStore.mediaPreviewSessionId 对齐，用于新一轮预览时重置一次性续播 */
  mediaPreviewSessionId?: number;
  /** 外部监听控制器状态，便于把控制条渲染在 `VideoCom` 容器之外。 */
  onControllerChange?: (controller: Controller) => void;
}

/**
 * @description 将秒数格式化为 `HH:mm:ss`（分、秒为当前小时内剩余部分，非总分钟数）。
 */
export function formateVideoTime(seconds: number) {
  const s = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
  const hour = Math.floor(s / 3600);
  const minute = Math.floor((s % 3600) / 60);
  const second = Math.floor(s % 60);
  const formateHour = hour < 10 ? `0${hour}` : hour;
  const formateMinute = minute < 10 ? `0${minute}` : minute;
  const formateSecond = second < 10 ? `0${second}` : second;
  return `${formateHour}:${formateMinute}:${formateSecond}`;
}

/**
 * @description 将秒数格式化为 `mm:ss`（总分钟数，适用于不足一小时的展示）。
 */
export function formateVideoTimeNoHour(seconds: number) {
  const s = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
  const minute = Math.floor(s / 60);
  const second = Math.floor(s % 60);
  const formateMinute = minute < 10 ? `0${minute}` : minute;
  const formateSecond = second < 10 ? `0${second}` : second;
  return `${formateMinute}:${formateSecond}`;
}
/** 从 xgplayer 或底层 video 元素获取时长（iOS/微信下 player.duration 可能延迟，需兼容 player.video） */
function getPlayerDuration(player: XGPlayer): number {
  const d = player.duration;
  if (d > 0 && Number.isFinite(d)) return d;
  const v = (player as any).video;
  if (v && typeof v.duration === 'number' && v.duration > 0 && Number.isFinite(v.duration)) return v.duration;
  return 0;
}

function useCustomControls(videoRef: React.MutableRefObject<XGPlayer | null>, videoOptions: VideComProps, playerReady: boolean) {
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(videoOptions.volume);

  const play = useCallback(() => videoRef.current?.play(), [videoRef]);
  const pause = useCallback(() => videoRef.current?.pause(), [videoRef]);
  const toggle = useCallback(() => {
    const player = videoRef.current;
    if (!player) return;

    // 播放完毕，重置当前时间
    if (player.ended) {
      player.currentTime = 0;
    }

    playing ? pause() : play();
  }, [pause, play, playing, videoRef]);

  const setPlayerVolume = useCallback((v: number) => {
    if (!videoRef.current) return;
    videoRef.current.volume = v;
    setVolume(v);
  }, [videoRef]);

  const seek = useCallback((time: number) => {
    if (videoRef.current && typeof time === 'number') {
      videoRef.current.currentTime = time;
      setCurrentTime(time); // 这可以同步 React 状态
    }
  }, [videoRef]);

  useEffect(() => {
    const player = videoRef.current;
    if (!player || !playerReady) return;

    // iOS/微信下 duration 可能很晚才就绪，轮询时间加长（参考西瓜播放器 API：player.video 为底层 media 对象）
    const isWeChat = /MicroMessenger/i.test(navigator.userAgent);
    const POLL_INTERVAL = 300;
    const POLL_MAX_COUNT = isWeChat ? 35 : 20; // 微信约 10.5s，其余约 6s
    let pollTimer: number | null = null;
    let pollCount = 0;
    const startDurationPoll = () => {
      if (pollTimer) return;
      pollCount = 0;
      pollTimer = window.setInterval(() => {
        pollCount += 1;
        const d = getPlayerDuration(player);
        if (d > 0) {
          setDuration(d);
          if (pollTimer) window.clearInterval(pollTimer);
          pollTimer = null;
          return;
        }
        if (pollCount >= POLL_MAX_COUNT && pollTimer) {
          window.clearInterval(pollTimer);
          pollTimer = null;
        }
      }, POLL_INTERVAL) as unknown as number;
    };
    const clearDurationPoll = () => {
      if (pollTimer) {
        window.clearInterval(pollTimer);
        pollTimer = null;
      }
    };

    const applyDuration = () => {
      const d = getPlayerDuration(player);
      if (d > 0) {
        setDuration(d);
        clearDurationPoll();
      } else {
        startDurationPoll();
      }
    };

    const videoLoadedHandler = (e?: any) => {
      console.timeEnd('[videoCom] 视频加载完成');
      applyDuration();
    };

    const durationSetHandler = () => {
      const d = getPlayerDuration(player);
      if (d > 0) {
        setDuration(d);
        clearDurationPoll();
        return;
      }
      // 兼容部分手机/微信 X5 内核（含 iPhone）下 duration 为 0：先轮询，再尝试短暂播放触发元数据
      setDuration(d);
      startDurationPoll();
      player.play().catch(() => {}).finally(() => {
        setTimeout(() => {
          player.pause();
          const after = getPlayerDuration(player);
          if (after > 0) {
            setDuration(after);
            clearDurationPoll();
          }
        }, 500);
      });
    };

    // 西瓜播放器：loadeddata 起播数据加载完成，iOS/微信下有时比 loadedmetadata 更早拿到 duration
    const loadedDataHandler = () => {
      applyDuration();
    };

    // 微信内：WeixinJSBridgeReady 后主动 load 以触发元数据/时长加载（iOS 微信常需此步骤，参考西瓜播放器与微信 H5 文档）
    let weixinReadyHandler: (() => void) | null = null;
    if (isWeChat && (player as any).video) {
      weixinReadyHandler = () => {
        try {
          ((player as any).video as HTMLVideoElement)?.load?.();
        } catch {
          // 忽略 load 失败（如已销毁）
        }
      };
      if (typeof (window as any).WeixinJSBridge !== 'undefined') {
        weixinReadyHandler();
      } else {
        document.addEventListener('WeixinJSBridgeReady', weixinReadyHandler, { once: true });
      }
    }

    const playHandler = () => {
      setPlaying(true);
    };
    const pauseHandler = () => {
      setPlaying(false);
    };

    const update = () => {
      setCurrentTime(player.currentTime);
      const d = getPlayerDuration(player);
      if (d > 0) setDuration(d);
    };

    player.on(Events.DURATION_CHANGE, videoLoadedHandler);
    player.on(Events.LOADED_METADATA, durationSetHandler);
    if (Events.LOADED_DATA) player.on(Events.LOADED_DATA, loadedDataHandler);
    player.on(Events.CANPLAY, videoLoadedHandler);
    player.on(Events.COMPLETE, videoLoadedHandler);
    player.on(Events.PLAY, playHandler);
    player.on(Events.PAUSE, pauseHandler);
    player.on(Events.TIME_UPDATE, update);
    // 添加错误事件监听
    player.on(Events.ERROR, (error: any) => {
      mtstatTrack('video_load_fail', {
        error_code: error.errorCode,
        error_msg: error.msg,
        error_url: error.src,
        error_all_message: error,
        // room_id: roomId,
        // task_id: taskId,
      });
      // Sentry.captureMessage('视频播放错误', {
      //   level: 'warning',
      //   extra: {
      //     url: videoOptions.watermarkUrl || videoOptions.url,
      //     postter: videoOptions.watermarkPoster || videoOptions.poster,
      //     error: JSON.stringify(error),
      //   },
      // });
      // 在这里处理播放失败逻辑，比如显示错误提示
    });

    // eslint-disable-next-line consistent-return
    return () => {
      clearDurationPoll();
      if (weixinReadyHandler) {
        document.removeEventListener('WeixinJSBridgeReady', weixinReadyHandler);
      }
      player.off(Events.DURATION_CHANGE, videoLoadedHandler);
      player.off(Events.LOADED_METADATA, durationSetHandler);
      if (Events.LOADED_DATA) player.off(Events.LOADED_DATA, loadedDataHandler);
      player.off(Events.CANPLAY, videoLoadedHandler);
      player.off(Events.COMPLETE, videoLoadedHandler);
      player.off(Events.PLAY, playHandler);
      player.off(Events.PAUSE, pauseHandler);
      player.off(Events.TIME_UPDATE, update);
      player.off(Events.ERROR, () => { });
    };
  }, [videoRef, videoOptions.url, playerReady]);
  function visibilitychangeFn() {
    if (document.visibilityState === 'visible') {
      pause();
    }
  }
  useEffect(() => {
    document.addEventListener('visibilitychange', visibilitychangeFn);
    return () => {
      document.removeEventListener('visibilitychange', visibilitychangeFn);
    };
  }, []);
  return useMemo(() => ({
    play, pause, toggle, playing, currentTime, duration, volume, setPlayerVolume, seek,
  }), [currentTime, duration, pause, play, playing, seek, setPlayerVolume, toggle, volume]);
}

/**
 * 视频播放器封装，负责初始化 xgplayer 并把播放控制能力暴露给上层自定义控件。
 */
function VideoCom(
  videoOptions: VideComProps,
  ref: React.ForwardedRef<VideoComHandle>,
) {
  const {
    className, videoClassName, url, watermarkUrl = '', autoplay,
    loop = false, closeVideoClick = false, children, volume = 0, containerStyle, poster = '', watermarkPoster = '', posterSize, width, height, draggable = false, fitVideoSize,
    crossOrigin,
    crossorigin,
    videoInit = true,
    videoFillMode = 'contain',
    playbackHandoff = null,
    mediaPreviewSessionId = 0,
    onControllerChange,
  } = videoOptions;
  const mergedCrossOrigin = crossOrigin ?? crossorigin;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<XGPlayer | null>(null);
  const [playerReady, setPlayerReady] = useState(false);
  const handoffConsumedRef = useRef(false);

  useEffect(() => {
    handoffConsumedRef.current = false;
  }, [url, watermarkUrl, mediaPreviewSessionId, playbackHandoff?.startSec, playbackHandoff?.shouldPlay, playbackHandoff?.muted, !!playbackHandoff]);

  useEffect(() => {
    if (!playbackHandoff || !playerReady) return undefined;
    let cancelled = false;
    let frames = 0;
    const maxFrames = 120;
    const tryApply = () => {
      if (cancelled || handoffConsumedRef.current) return;
      const player = playerRef.current;
      if (!player) return;
      const d = getPlayerDuration(player);
      if (d > 0) {
        const maxT = Math.max(0, d - 0.05);
        const t = Math.min(Math.max(0, playbackHandoff.startSec), maxT);
        try {
          player.currentTime = t;
        } catch {
          // 部分环境下 seek 可能抛错，忽略
        }
        if (playbackHandoff.muted === true) {
          try {
            player.volume = 0;
            const vid = (player as { video?: HTMLVideoElement; }).video;
            if (vid) {
              vid.muted = true;
              vid.volume = 0;
            }
          } catch {
            // 忽略部分环境下的赋值失败
          }
        }
        if (playbackHandoff.shouldPlay) {
          player.play().catch(() => {});
        }
        handoffConsumedRef.current = true;
        return;
      }
      frames += 1;
      if (frames < maxFrames) {
        requestAnimationFrame(tryApply);
      }
    };
    const id = requestAnimationFrame(tryApply);
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [playerReady, url, watermarkUrl, playbackHandoff, mediaPreviewSessionId]);

  useEffect(() => {
    console.time('[videoCom] 视频加载完成');
    if (!videoRef.current) return () => {};

    let player: XGPlayer | null = null;
    let isCancelled = false;

    const finalUrl = watermarkUrl || url;
    const finalPoster = handleImageUrl(poster, { size: posterSize, showWatermark: !!watermarkPoster });

    const initPlayer = async () => {
      // 等待封面图加载完成
      if (finalPoster) {
        try {
          await preloadImages([finalPoster]);
        } catch (e) {
          // ignore image load error
        }
      }

      // 检查是否已取消或 dom 不存在
      if (isCancelled || !videoRef.current) return;

      player = new XGPlayer({
        el: videoRef.current,
        url: finalUrl,
        'x5-video-player-type': 'h5',
        volume,
        autoplay,
        loop,
        poster: finalPoster,
        videoInit,
        // 关闭默认控件
        controls: false,
        ignores: ['replay', 'start', 'mobile'],
        width: width || '100%',
        height: height || '100%',
        fitVideoSize: fitVideoSize || 'fixed',
        videoFillMode,
        videoAttributes: {
          preload: 'auto',
          draggable,
          crossorigin: mergedCrossOrigin,
          crossOrigin: mergedCrossOrigin,
        },
        keyShortcut: false, // 关闭快捷键
        closeVideoDblclick: true, // 关闭双击全屏
        closeVideoClick, // 是否关闭单击播放器区域切换播放/暂停
        closeVideoTouch: true,
        enableVideoDbltouch: true,
      });

      // 修复封面图被拉伸的问题；与 videoFillMode=cover 时封面也铺满，避免加载前后四边留白不一致
      if (player.root) {
        const posterDom = player.root.querySelector('.xgplayer-poster') as HTMLElement;
        if (posterDom) {
          posterDom.style.backgroundSize = videoFillMode === 'cover' ? 'cover' : 'contain';
        }
      }

      playerRef.current = player;
      setPlayerReady(true);
    };

    initPlayer();

    return () => {
      isCancelled = true;
      setPlayerReady(false);
      if (player) {
        player.destroy();
      }
      playerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, watermarkUrl, width, height, videoInit, videoFillMode]);

  useEffect(() => {
    if (!playerReady || !autoplay) return;
    playerRef.current?.play().catch(() => {});
  }, [playerReady, autoplay]);

  const constrols = useCustomControls(playerRef, videoOptions, playerReady);
  // 下载
  const onDownload = useCallback(({
    location,
    hasWatermark,
    task_id = '',
  }: StatParams) => {
    try {
      mtstatTrack('canvas_save_click', {
        location,
        save_type: 'video',
        video_num: 1,
        file_type: '',
        file_size: '',
        url,
        task_id,
      });
      if (hasWatermark === '2') return;
      downLoad(hasWatermark === '1' && watermarkUrl ? watermarkUrl : url);
      mtstatTrack('canvas_save_success', {
        location,
        save_type: 'video',
        video_num: 1,
        file_type: '',
        file_size: '',
        url,
        ar_mark: hasWatermark,
        task_id,
      });
    } catch (error) {
      mtstatTrack('canvas_save_fail', {
        location,
        save_type: 'video',
        video_num: 1,
        file_type: '',
        file_size: '',
        url,
        ar_mark: hasWatermark,
        task_id,
      });
    }
  }, [url, watermarkUrl]);
  const controller = useMemo(() => ({
    ...constrols,
    onDownload,
  }), [constrols, onDownload]);

  useEffect(() => {
    onControllerChange?.(controller);
  }, [controller, onControllerChange]);

  useImperativeHandle(ref, () => ({
    containerRef: containerRef.current,
    playerRef: playerRef.current,
    videoRef: videoRef.current,
  }));

  return (
    <div style={containerStyle} className={className} ref={containerRef}>
      <div
        ref={videoRef}
        className={videoClassName}
        style={{
          background: 'transparent',
          position: 'relative',
          width,
          height,
        }}
      />
      {/* 控件区域 */}
      {children?.(controller)}

    </div>

  );
}
export default memo(forwardRef(VideoCom));
